import { useState } from "react";
import { motion } from "framer-motion";
import { Flag, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import SectionHeading from "@/components/SectionHeading";
import { useSectionContent } from "@/hooks/useContent";

const FIELDS = [
  { name: "name", label: "Nome e Cognome", required: true, testid: "membership-name-input" },
  { name: "email", label: "Email", type: "email", required: true, testid: "membership-email-input" },
  { name: "phone", label: "Telefono", type: "tel", testid: "membership-phone-input" },
  { name: "bike", label: "La tua Moto (modello e cilindrata)", testid: "membership-bike-input" },
];

export default function DiventaSocio() {
  const c = useSectionContent("membership");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", bike: "", experience: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/memberships", form);
      toast.success("Richiesta inviata! Il direttivo ti ricontatterà presto.");
      setForm({ name: "", email: "", phone: "", bike: "", experience: "", message: "" });
    } catch {
      toast.error("Invio non riuscito. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="diventa-socio"
      data-testid="membership-section"
      className="py-24 sm:py-32 px-5 sm:px-8 bg-[#121215]"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionHeading
            overline={c.overline}
            title={c.title}
            description={c.description}
            testid="membership-heading"
          />
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 space-y-4"
          >
            {[
              "Passione vera per le due ruote e la strada",
              "Rispetto del regolamento del club",
              "Voglia di esserci: giri, raduni e assemblee",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#C0B9B0]">
                <Flag className="w-4 h-4 text-[#C5A059] shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="leather-card p-7 space-y-5"
          data-testid="membership-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.name}>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
                  {f.label}
                </label>
                <input
                  data-testid={f.testid}
                  required={f.required}
                  type={f.type || "text"}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] transition-colors"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
              Da quanto vai in moto
            </label>
            <select
              data-testid="membership-experience-select"
              value={form.experience}
              onChange={(e) => setForm({ ...form, experience: e.target.value })}
              className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]"
            >
              <option value="">Scegli...</option>
              <option value="Meno di 1 anno">Meno di 1 anno</option>
              <option value="1-5 anni">1-5 anni</option>
              <option value="5-15 anni">5-15 anni</option>
              <option value="Oltre 15 anni">Oltre 15 anni</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
              Presentati (opzionale)
            </label>
            <textarea
              data-testid="membership-message-input"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] transition-colors resize-none"
            />
          </div>
          <button
            data-testid="membership-submit-button"
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {loading ? "Invio in corso..." : "Invia Richiesta di Adesione"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
